import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import Badge from '../../../UniformComponents/Badge'
import Button from '../../../UniformComponents/Button'
import Typography from '../../../UniformComponents/Typography'
import './aiSummaryWidget.scss'
import BubbleLoader from '../../../UniformComponents/BubbleLoader'

const PAGE_TO_RESPONSE_TYPE = {
    company: 'SE',
    exhibitor: 'SI',
}

const ERROR_MESSAGES = {
    no_responses_yet: i18n.t('aiwidget.error.no_responses_yet'),
    not_enough_responses: i18n.t('aiwidget.error.not_enough_responses'),
    flags_disabled: i18n.t('aiwidget.error.flags_disabled'),
    invalid_parameters: i18n.t('aiwidget.error.invalid_parameters'),
    summary_failed: i18n.t('aiwidget.error.summary_failed'),
    manual_refresh_limit: i18n.t('aiwidget.error.manual_refresh_limit'),
    ai_summary_running: i18n.t('aiwidget.error.ai_summary_running'),
    default: i18n.t('aiwidget.error.default'),
}

const AiSummaryWidget = observer(({
    surveyId, appStore, filterString, exhibitorName, pageType, shared
}) => {
    const [view, setView] = useState('idle') // idle | loading | canGenerate | generating | done | error
    const [summary, setSummary] = useState(null)
    const [errorStatus, setErrorStatus] = useState(null)
    const [pollTimer, setPollTimer] = useState(null)
    const [polling, setPolling] = useState(false)

    const responseType = PAGE_TO_RESPONSE_TYPE[pageType] || 'SI'

    const startPolling = () => {
        let attempts = 0
        const MAX_ATTEMPTS = 12 // 12 × 5s = 60s max
        const poll = async () => {
            if (attempts >= MAX_ATTEMPTS) {
                setView('error')
                setErrorStatus('default')
                return
            }
            attempts += 1
            try {
                const res = await appStore.http.post(`/dashboard/summary${shared ? '/shared' : ''}/${surveyId}/${responseType}`,
                    JSON.stringify({
                        triggerSource: 'dashboard',
                        filterString: filterString ?? '',
                    }))
                if (res?.summary) {
                    setSummary(res.summary)
                    setView('done')
                    return
                }
                const stillRunning = !res?.result && ['ai_summary_running', 'no_existing_summary'].includes(res?.status)
                if (!stillRunning) {
                    setView('error')
                    setErrorStatus(res?.status || 'default')
                    return
                }
                setPollTimer(setTimeout(poll, 5000))
            } catch (e) {
                setView('error')
                setErrorStatus('default')
            }
        }
        poll()
    }

    // const generate = async () => {
    //     if (!surveyId) return
    //     setView('generating')
    //     const filter = dashboardStore.getActiveFilters(widget.respondentType, widget.includeRegistrationData)
    //     try {
    //         await appStore.http.post(
    //             `/dashboard/summary/generate/${responseType}`,
    //             JSON.stringify({
    //                 triggerSource: 'dashboard',
    //                 surveyId,
    //                 filter,
    //                 filterString: dashboardStore.dashboardFilters?.[0]?.options?.flatMap((o) => o.surveyDataIds)?.join('_') ?? '',
    //             }),
    //         )
    //         startPolling()
    //     } catch (e) {
    //         setView('error')
    //         setErrorStatus('default')
    //     }
    // }
    if (!polling) {
        setView('loading')
        setPolling(true)
        startPolling()
    }

    const renderStateContent = () => {
        switch (view) {
            case 'loading':
                return (
                    <div style={{
                        height: '390px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                    >
                        <Typography type="h2">
                            Generating AI Summary
                        </Typography>
                        <BubbleLoader />
                    </div>
                )
            // case 'canGenerate':
            //     return <Button variant="secondary" label="Generate" onClick={generate} />
            case 'generating':
                return (
                    <>
                        <i className="fa-solid fa-spinner fa-spin" />
                        {i18n.t('s_report_ai_summary_not_ready')}
                    </>
                )
            case 'error':
                return ERROR_MESSAGES[errorStatus] || ERROR_MESSAGES.default
            default:
                return null
        }
    }

    const stateClass = `ai-summary-widget__state${view === 'error' ? ' ai-summary-widget__state--error' : ''}`

    return (
        <div className="ai-summary-widget">
            <Badge
                isRounded
                type="success"
                label="AI generated"
                style={{
                    position: 'absolute', top: 8, right: 8, border: 'solid 1px #c1c1c1',
                }}
            />
            {view === 'done' && summary
                ? <SummaryContent summary={summary} exhibitorName={exhibitorName} pageType={pageType} />
                : <div className={stateClass}>{renderStateContent()}</div>}

        </div>
    )
})

const renderBoldSplit = (text) => text
    .split(/<SPLIT HERE>/g)
    .map((part, i) => (i === 1 ? [
        <>
            <br />
            <br />
        </>,
        part,
    ] : part))

const renderBold = (text) => text
    .split(/<mark>(.*?)<\/mark>/g)
    .map((part, i) => (i % 2 === 1 ? <b key={part}>{part}</b> : renderBoldSplit(part))).flat()

const SummaryContent = ({ summary, exhibitorName, pageType }) => {
    const overview = summary?.fieldOverview ?? (typeof summary === 'string' ? summary : null)
    const focusPoints = summary?.focusPoints ?? []

    const title = pageType === 'company'
        ? 'Overall event summary'
        : `${exhibitorName} - Exhibitor Insights Agent`

    return (
        <div className="ai-summary-widget__content">
            {overview && (
                <>
                    <Typography type="b" cssClass="ai-summary-widget__overview-title">
                        {title}
                    </Typography>
                    <Typography type="p">{renderBold(overview)}</Typography>
                </>
            )}
            {focusPoints.length > 0 && (
                <div className="bulletPoints">
                    <Typography
                        type="tiny-b"
                        color="primary"
                        cssClass="ai-summary-widget__focus-label"
                    >
                        {i18n.t('dashboard.aiWidget.title.2')}
                    </Typography>
                    <ul className="ai-summary-widget__focus-list">
                        {focusPoints.map((point, i) => (
                            <li key={i} className="ai-summary-widget__focus-item">
                                <i className="fa-solid fa-circle-small" />
                                <Typography type="p">{renderBold(point)}</Typography>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {!overview && !focusPoints.length && (
                <Typography type="p">
                    {typeof summary === 'string' ? summary : JSON.stringify(summary, null, 2)}
                </Typography>
            )}
            <div className="disclaimer" style={{ fontSize: '11px' }}>
                Ai-generated content insights may contain inaccuracies. Always review underlying data.
            </div>
        </div>
    )
}

export default AiSummaryWidget
